import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";

import httpService from "V2.0/services/httpService";
import authService from "V2.0/services/authService";

import { CONVERSATION } from "../../models/Conversation";
import { useWhatsappSend } from "../../models/useWhatsappSend";
import { useInstagramSend } from "../../models/useInstagramSend";
import { useMessengerSend } from "../../models/useMessengerSend";
import { ConversationMoreOptions } from "../conversation_more_options";

import { ChatEditor } from "V2.0/HomePages/Threads";
import { Button } from "V2.0/common/button";

import { AgentAssign, AssignmentMode } from "../agent_reassignment";
import { ResolveConversation } from "../conversation_resolve";
import { ConversationSourceIcon, SourceIcon } from "../conversation_item";
import { checkForMediaMessage, MediaMessages } from "./Whatsapp";
import {
  checkForInstagramMediaMessage,
  InstagramMediaMessages,
} from "./Instagram/MediaMessage";
import { Badge } from "V2.0/common/badge";
import useAgentAssignment from "./useAgentAssignment";
import useAgentAssignedConversation from "./useAgentAssignedConversation";
import { showErrorToast } from "V2.0/utils";
import {
  useConversationMessagesTabs,
  MESSAGE_TABS,
  useWhatsappConversationTabs,
  WHATSAPP_TABS,
} from "./useConversationMessagesTabs";
import TabsWithBadge from "V2.0/common/tabsWithBadge";
import Notes from "../notes";
import { useOutletContext, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ChatBubbleLeftRightIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {
  PhoneArrowDownLeftIcon,
  PhoneArrowUpRightIcon,
} from "@heroicons/react/24/solid";
import { Tooltip } from "antd";
import { archiveConversation } from "./helpers/archiveConversation";
import { postAddToSMSThread } from "V2.0/services/smsService";
import clsx from "clsx";
import useWhatsappMessageViewModel from "./Whatsapp/useWhatsappMessageViewModel";
import useAgentTranscription from "./Voice/useAgentTranscription";
import { MdSupportAgent } from "react-icons/md";
import { TurnMetricsLine, ContextChangeMarker, resolveTurnMetric, resolveContextChanges } from "../turn_metrics/TurnMetricsLine";

const STATUS = {
  failed: {
    label: "Failed",
    color: "red",
  },
  read: {
    label: "Read",
    color: "green",
  },
  delivered: {
    label: "Delivered",
    color: "green",
  },
  sent: {
    label: "Sent",
    color: "amber",
  },
  queued: {
    label: "Queued",
    color: "amber",
  },
};

export function formatTimestamp(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);

  const timeDiff = now - date;
  const isWithinDay = timeDiff < 24 * 60 * 60 * 1000;

  const formattedDate = date.toLocaleString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return formattedDate;
}

export const useConversationById = (conversationData) => {
  const [conversation, setConversation] = useState(conversationData);

  const fetchConversationById = async (projectId, conversationId) => {
    try {
      const response = await httpService.get(
        `/conversation/whitelabelGetConversation/${projectId}/${conversationId}`,
      );
      if (response.status === 200) {
        setConversation(response.data);
      }
    } catch (error) {
      showErrorToast(error, "Failed to get conversation");
      console.error(error);
    }
  };

  return {
    conversation,
    setConversation,
    fetchConversationById,
  };
};

export const ConversationMessage = {
  [CONVERSATION.EMBEDDED_CHAT]: ({ key, ...restProps }) => (
    <ConversationMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.INBOUND_VOICE]: ({ key, ...restProps }) => (
    <VoiceConversationMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.OUTBOUND_VOICE]: ({ key, ...restProps }) => (
    <VoiceConversationMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.SMS]: ({ key, ...restProps }) => (
    <SmsMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.WEB_CALL]: ({ key, ...restProps }) => (
    <ConversationMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.WHATSAPP]: ({ key, ...restProps }) => (
    <WhatsappMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.MESSENGER]: ({ key, ...restProps }) => (
    <MessengerMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.API]: ({ key, ...restProps }) => (
    <ConversationMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.INSTAGRAM]: ({ key, ...restProps }) => (
    <InstagramMessagesView
      key={key}
      {...restProps}
    />
  ),
  [CONVERSATION.MCUBE_ENGAGE_WHATSAPP]: ({ key, ...restProps }) => (
    <MCubeEngageWhatsappMessageView
      key={key}
      {...restProps}
    />
  ),
};

export function ConversationMessageView() {
  const {
    selectedConversation,
    conversations,
    updateSelectedConversation,
    syncConversation,
    projectId,
    setShowDetails,
    loading,
    pushConversation,
    scrollToView,
    setScrollToView,
    ...restProps
  } = useOutletContext();

  const { conversationId } = useParams();
  const prevProjectIdRef = useRef("");
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToChild = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollToView(false);
      setShowDetails(true);
    } else {
      setTimeout(() => {
        scrollToChild(id);
      }, 200);
    }
  };

  const fetchConversationById = async (projectId, conversationId) => {
    try {
      const response = await httpService.get(
        `/conversation/whitelabelGetConversation/${projectId}/${conversationId}`
      );
      if (response.status === 200) {
        pushConversation(response.data);
      }
    } catch (error) {
      showErrorToast(error, "Failed to get conversation");
      console.error(error);
    }
  };

  const selectConversation = () => {
    const conversation = conversations.find(
      (conversation) => conversation._id === conversationId,
    );

    if (conversation) {
      updateSelectedConversation(conversation);

      if (scrollToView) {
        scrollToChild(conversation._id);
      }

      return;
    }

    if (projectId === prevProjectIdRef.current) {
      fetchConversationById(projectId, conversationId);
    } else {
      navigate({
        pathname: "/threads",
        search: location.search
      });
    }
  };

  useEffect(() => {
    if (!conversationId) {
      updateSelectedConversation({});

      return;
    }

    if (projectId && !loading) {
      selectConversation();
    }
  }, [conversations, projectId, loading, conversationId]);

  useEffect(() => {
    if (!prevProjectIdRef.current && projectId) {
      prevProjectIdRef.current = projectId;
    }
  }, [projectId]);

  useEffect(() => {
    if (selectedConversation?._id && scrollToView) {
      scrollToChild(selectedConversation._id);
    }
  }, [selectedConversation, scrollToView]);

  if (selectedConversation?.sourceOfConversation && !scrollToView) {
    return ConversationMessage[selectedConversation?.sourceOfConversation]({
      selectedConversation,
      syncConversation,
      loading,
      ...restProps,
    });
  }

  return (
    <div className="flex flex-col my-auto items-center mx-auto">
      <ChatBubbleLeftRightIcon className="size-16" />
      <p>No Thread Selected</p>
    </div>
  );
}

function useWhatsappSelected(whatsAppConversation, user) {
  const { status, humanAgentAssignmentSettings, messages } =
    whatsAppConversation;

  const lastMessage = messages[messages.length - 1];
  const lastMessageTimestamp =
    lastMessage?.aTimestamp || lastMessage?.qTimestamp;
  const is24HoursElapsedSinceLastMessage =
    new Date().getTime() - lastMessageTimestamp > 24 * 60 * 60 * 1000;

  const isHumanAgentAssigned =
    !!humanAgentAssignmentSettings?.humanAgentAssigned;

  const isActiveUserTheHumanAgent =
    isHumanAgentAssigned &&
    humanAgentAssignmentSettings.humanAgentAssigned?.email === user.email &&
    user?.userAccessLevel != "WHITELABELVIEWER";

  const isConversationActive = status === "ACTIVE";

  const isActiveUserCompanyOwner = user?.userAccessLevel === "WHITELABELOWNER";

  const allowActiveUserToSendMessage =
    isHumanAgentAssigned &&
    isActiveUserTheHumanAgent &&
    isConversationActive &&
    !is24HoursElapsedSinceLastMessage;

  const allowActiveUserToReassign =
    isHumanAgentAssigned &&
    (isActiveUserTheHumanAgent || isActiveUserCompanyOwner) &&
    isConversationActive;

  const allowActiveUserToResolve =
    isHumanAgentAssigned &&
    (isActiveUserTheHumanAgent || isActiveUserCompanyOwner) &&
    isConversationActive;

  const allowActiveUserToAssign = !isHumanAgentAssigned && isConversationActive;

  const allowActiveUserToStartWithATemplate =
    isHumanAgentAssigned &&
    isActiveUserTheHumanAgent &&
    isConversationActive &&
    is24HoursElapsedSinceLastMessage;

  return {
    allowActiveUserToSendMessage,
    allowActiveUserToReassign,
    allowActiveUserToResolve,
    allowActiveUserToAssign,
    allowActiveUserToStartWithATemplate,
    isConversationActive,
    isHumanAgentAssigned,
  };
}

function WhatsappMessagesView(props) {
  const {
    selectedConversation,
    onDeleteConversation,
    onRetryExtraction,
    onViewContactClick,
    fetchConversations,
    resetSelectedConversation,
    refreshConversations,
    assistants,
    users,
    syncConversation,
    isViewOnly,
    turnMetrics,
  } = props;

  const { activeTab, setActiveTab, tabs } = useWhatsappConversationTabs();
  const user = authService.getCurrentUser();
  const [whatsappConversation, setWhatsappConversation] = useState(
    selectedConversation || {},
  );
  const [assignAgent, setAssignAgent] = useState({
    isOpen: false,
    mode: "",
  });
  const [expandCollapseCallBlocks, setExpandCollapseCallBlocks] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    setWhatsappConversation(selectedConversation);
  }, [selectedConversation]);

  const {
    allowActiveUserToAssign,
    allowActiveUserToReassign,
    allowActiveUserToResolve,
    allowActiveUserToSendMessage,
    allowActiveUserToStartWithATemplate,
    isConversationActive,
    isHumanAgentAssigned,
  } = useWhatsappSelected(selectedConversation, user);
  const { sendWhatsappMessage } = useWhatsappSend();

  const fetchWhatsappConversationById = async (projectId, conversationId) => {
    try {
      const response = await httpService.get(
        `/conversation/whitelabelGetConversation/${projectId}/${conversationId}`,
      );
      if (response.status === 200) {
        syncConversation(response.data);
        scrollRef?.current && scrollRef.current.scrollToBottom();
      }
    } catch (error) {
      console.error(error);
    }
  };

  function updateLocalMessage(message) {
    const updatedConversation = {
      ...whatsappConversation,
      messages: [
        ...whatsappConversation.messages,
        {
          humanAgent: user.userId,
          q: "",
          a: message,
          aTimestamp: new Date().getTime(),
        },
      ],
    };

    syncConversation(updatedConversation);
    scrollRef?.current && scrollRef.current.scrollToBottom();
  }

  const listener = useCallback(() => {
    fetchWhatsappConversationById(
      selectedConversation?.workspace,
      selectedConversation._id,
    );
  }, [selectedConversation]);

  const { messages: messagesViewModel, calls: callsViewModel } =
    useWhatsappMessageViewModel(whatsappConversation?.messages ?? []);

  const agentId =
    whatsappConversation?.humanAgentAssignmentSettings?.humanAgentAssigned?._id;

  const handleArchiveConversation =
    isConversationActive && !isHumanAgentAssigned
      ? () => archiveConversation(selectedConversation, syncConversation)
      : null;

  return (
    <MessageView key={selectedConversation._id}>
      <MessageViewHeader>
        <div className="flex justify-between items-center">
          <MessageViewContact onContactClick={onViewContactClick}>
            <SourceIcon>
              {
                ConversationSourceIcon[
                  selectedConversation.sourceOfConversation
                ]
              }
            </SourceIcon>
            <MessageViewHeaderTitle>
              {whatsappConversation?.userIdentifier ||
                whatsappConversation?.sourceOfConversation}
            </MessageViewHeaderTitle>
          </MessageViewContact>
          <div className="flex gap-x-2 items-center">
            {allowActiveUserToAssign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.ASSIGN,
                  })
                }
              >
                Assign
              </Button>
            )}
            {allowActiveUserToResolve && (
              <ResolveConversation
                projectId={selectedConversation.workspace}
                conversationId={selectedConversation._id}
                fetchConversations={fetchConversations}
                resetSelectedConversation={resetSelectedConversation}
              />
            )}
            {allowActiveUserToReassign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.RE_ASSIGN,
                  })
                }
              >
                Reassign
              </Button>
            )}
            {!isViewOnly && (
              <ConversationMoreOptions
                onDeleteConversation={onDeleteConversation}
                onRetryExtraction={onRetryExtraction}
                onArchiveConversation={handleArchiveConversation}
              />
            )}
          </div>
        </div>
        <MessageViewHeaderTabs
          tabs={tabs}
          updateActiveTab={setActiveTab}
        />
      </MessageViewHeader>
      <MessageViewBody ref={scrollRef}>
        {activeTab === MESSAGE_TABS.MESSAGES && (
          <div className="p-4 space-y-6">
            {messagesViewModel.map((message, index) => {
              const { type } = message;

              if (type === "message") {
                const { message: item } = message;
                return (
                  <React.Fragment key={index}>
                    <RichTextMessage
                      className="flex justify-start"
                      message={item?.q ?? ""}
                      timestamp={item?.qTimestamp}
                      user={false}
                      projectId={selectedConversation.workspace}
                      conversationId={selectedConversation._id}
                      sourceOfConversation={
                        selectedConversation.sourceOfConversation
                      }
                    />
                    <ContextChangeMarker changes={resolveContextChanges(turnMetrics, message.index)} />
                    <RichTextMessage
                      className="flex justify-end"
                      message={item.a}
                      timestamp={item?.aTimestamp}
                      user={true}
                      projectId={selectedConversation.workspace}
                      conversationId={selectedConversation._id}
                      status={item?.whatsAppDetails?.aWStatus}
                      errorMessage={item?.whatsAppDetails?.errorMessage}
                      sourceOfConversation={
                        selectedConversation.sourceOfConversation
                      }
                      assistant={
                        Boolean(item?.assistant)
                          ? assistants[item.assistant]
                          : ""
                      }
                      humanAgent={
                        Boolean(item?.humanAgent) ? users[item.humanAgent] : ""
                      }
                    />
                    <TurnMetricsLine
                      metric={resolveTurnMetric(turnMetrics, message.index)}
                      channel={turnMetrics?.channel}
                    />
                  </React.Fragment>
                );
              }

              if (type === "call") {
                const { messages, callId, start, end, duration, direction } =
                  message;

                const _messages = messages.map((message, idx) => {
                  const { message: item } = message;
                  return (
                    <React.Fragment key={idx}>
                      <Message
                        className="flex justify-start"
                        message={item?.q || ""}
                        timestamp={item?.qTimestamp}
                        user={false}
                        customBubbleColor="bg-blue-200 text-gray-900"
                      />
                      <Message
                        className="flex justify-end"
                        message={item?.a || ""}
                        timestamp={item?.aTimestamp}
                        user={true}
                        customBubbleColor="bg-blue-600 text-white"
                      />
                    </React.Fragment>
                  );
                });

                return (
                  <div
                    key={callId}
                    className={`${clsx(
                      "mx-auto w-full",
                      expandCollapseCallBlocks[callId] &&
                        "bg-blue-50 border border-blue-100 rounded-lg",
                    )}`}
                  >
                    {!expandCollapseCallBlocks[callId] && (
                      <div className="w-full">
                        <div className="flex bg-green-100 gap-x-2 border border-green-100 rounded-lg justify-between items-center p-2">
                          <div className="flex gap-x-2 items-start">
                            <CallDirectionIcon direction={direction} />
                            <div className="space-y-2">
                              <p className="text-xs">
                                {formatTimestamp(start)}
                              </p>
                              <p className="text-xs">{duration}</p>
                            </div>
                          </div>
                          <Button
                            plain
                            onClick={() =>
                              setExpandCollapseCallBlocks((prevState) => {
                                return {
                                  ...prevState,
                                  [callId]: !prevState[callId],
                                };
                              })
                            }
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    )}
                    {expandCollapseCallBlocks[callId] ? (
                      <>
                        <div className="flex justify-between bg-white rounded-t-lg p-2 border-b border-blue-50">
                          <div className="flex gap-x-4 items-start">
                            <CallDirectionIcon direction={direction} />
                            <div className="flex flex-col gap-y-2">
                              <p className="text-sm">
                                {formatTimestamp(start)}
                              </p>
                              <p className="text-sm">{duration}</p>
                            </div>
                          </div>
                          <Button
                            plain
                            onClick={() =>
                              setExpandCollapseCallBlocks((prevState) => {
                                return {
                                  ...prevState,
                                  [callId]: !prevState[callId],
                                };
                              })
                            }
                          >
                            Collapse
                          </Button>
                        </div>
                        <div className="p-4 space-y-6">
                          {expandCollapseCallBlocks[callId] ? _messages : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                );
              }
            })}
          </div>
        )}
        {activeTab === MESSAGE_TABS.NOTES && (
          <div className="bg-zinc-50 w-full h-full">
            <Notes
              users={users}
              notes={whatsappConversation?.notes}
              conversationId={whatsappConversation._id}
              workspaceId={whatsappConversation.workspace}
              syncConversation={syncConversation}
              isViewOnly={isViewOnly}
            />
          </div>
        )}
        {activeTab === WHATSAPP_TABS.CALLS && (
          <div className=" w-full h-full bg-white">
            <div className="flex-1 overflow-scroll h-full space-y-2 p-4 bg-gray-50">
              {callsViewModel.map((call, index) => {
                const { messages, callId, start, end, duration, direction } =
                  call;

                const _messages = messages.map((message, idx) => {
                  const { message: item } = message;
                  return (
                    <React.Fragment key={idx}>
                      <Message
                        className="flex justify-start"
                        message={item?.q || ""}
                        timestamp={item?.qTimestamp}
                        user={false}
                        customBubbleColor="bg-blue-200 text-gray-900"
                      />
                      <Message
                        className="flex justify-end"
                        message={item?.a || ""}
                        timestamp={item?.aTimestamp}
                        user={true}
                        customBubbleColor="bg-blue-600 text-white"
                      />
                    </React.Fragment>
                  );
                });

                return (
                  <div
                    key={callId}
                    className="space-y-2 bg-white border rounded-lg"
                  >
                    {!expandCollapseCallBlocks[callId] && (
                      <div className="flex justify-between items-center p-4">
                        <div className="flex gap-x-4 items-start">
                          <CallDirectionIcon direction={call.direction} />
                          <div className="flex flex-col gap-y-2">
                            <p className="text-sm">
                              {formatTimestamp(call.start)}
                            </p>
                            <p className="text-sm">{call.duration}</p>
                          </div>
                        </div>
                        <Button
                          plain
                          onClick={() =>
                            setExpandCollapseCallBlocks((prevState) => {
                              return {
                                ...prevState,
                                [callId]: !prevState[callId],
                              };
                            })
                          }
                        >
                          View
                        </Button>
                      </div>
                    )}
                    {expandCollapseCallBlocks[callId] ? (
                      <>
                        <div className="flex p-4 justify-between bg-white rounded-t-lg border-b border-blue-50">
                          <div className="flex gap-x-4 items-start">
                            <CallDirectionIcon direction={call.direction} />
                            <div className="flex flex-col gap-y-2">
                              <p className="text-sm">
                                {formatTimestamp(call.start)}
                              </p>
                              <p className="text-sm">{call.duration}</p>
                            </div>
                          </div>
                          <Button
                            className="flex-none"
                            plain
                            onClick={() =>
                              setExpandCollapseCallBlocks((prevState) => {
                                return {
                                  ...prevState,
                                  [callId]: !prevState[callId],
                                };
                              })
                            }
                          >
                            Collapse
                          </Button>
                        </div>
                        <div className="p-4 space-y-6">
                          {expandCollapseCallBlocks[callId] ? _messages : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </MessageViewBody>
      {allowActiveUserToSendMessage && (
        <MessageFooter>
          <ChatEditor
            conversation={whatsappConversation}
            updateLocalMessage={updateLocalMessage}
            onSuccess={listener}
            onSend={sendWhatsappMessage}
          />
        </MessageFooter>
      )}
      {allowActiveUserToStartWithATemplate && <MessageFooter></MessageFooter>}
      {assignAgent.isOpen && (
        <AgentAssign
          mode={assignAgent.mode}
          isOpen={assignAgent.isOpen}
          onClose={() =>
            setAssignAgent({
              isOpen: false,
              mode: "",
            })
          }
          agentId={agentId}
          projectId={selectedConversation.workspace}
          conversationId={selectedConversation._id}
          fetchConversations={fetchConversations}
          resetSelectedConversation={resetSelectedConversation}
          assistantId={selectedConversation.assistant}
        />
      )}
    </MessageView>
  );
}

function MCubeEngageWhatsappMessageView(props) {
  const {
    selectedConversation,
    onDeleteConversation,
    onRetryExtraction,
    onViewContactClick,
    fetchConversations,
    resetSelectedConversation,
    refreshConversations,
    assistants,
    users,
    syncConversation,
    isViewOnly,
  } = props;

  const { activeTab, setActiveTab, tabs } = useConversationMessagesTabs();
  const user = authService.getCurrentUser();
  const [whatsappConversation, setWhatsappConversation] = useState(
    selectedConversation || {},
  );
  const [assignAgent, setAssignAgent] = useState({
    isOpen: false,
    mode: "",
  });

  useEffect(() => {
    setWhatsappConversation(selectedConversation);
  }, [selectedConversation]);

  const {
    allowActiveUserToAssign,
    allowActiveUserToReassign,
    allowActiveUserToResolve,
    allowActiveUserToSendMessage,
    allowActiveUserToStartWithATemplate,
    isConversationActive,
    isHumanAgentAssigned,
  } = useWhatsappSelected(selectedConversation, user);
  const { sendMCubeEngageWhatsappMessage } = useWhatsappSend();

  const fetchWhatsappConversationById = async (projectId, conversationId) => {
    try {
      const response = await httpService.get(
        `/conversation/whitelabelGetConversation/${projectId}/${conversationId}`,
      );
      if (response.status === 200) {
        setWhatsappConversation(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  function updateLocalMessage(message) {
    setWhatsappConversation((prevState) => {
      const messages = [...prevState.messages];

      return {
        ...prevState,
        messages: [
          ...messages,
          {
            q: " ",
            a: message,
            aTimestamp: new Date().getTime(),
          },
        ],
      };
    });
  }

  const listener = useCallback(() => {
    fetchWhatsappConversationById(
      selectedConversation?.workspace,
      selectedConversation._id,
    );
  }, [selectedConversation]);

  const agentId =
    whatsappConversation?.humanAgentAssignmentSettings?.humanAgentAssigned?._id;

  const handleArchiveConversation =
    isConversationActive && !isHumanAgentAssigned
      ? () => archiveConversation(selectedConversation, syncConversation)
      : null;

  return (
    <MessageView key={selectedConversation._id}>
      <MessageViewHeader>
        <div className="flex justify-between items-center">
          <MessageViewContact onContactClick={onViewContactClick}>
            <SourceIcon>
              {
                ConversationSourceIcon[
                  selectedConversation.sourceOfConversation
                ]
              }
            </SourceIcon>
            <MessageViewHeaderTitle>
              {whatsappConversation?.userIdentifier ||
                whatsappConversation?.sourceOfConversation}
            </MessageViewHeaderTitle>
          </MessageViewContact>
          <div className="flex gap-x-2 items-center">
            {allowActiveUserToAssign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.ASSIGN,
                  })
                }
              >
                Assign
              </Button>
            )}
            {allowActiveUserToResolve && (
              <ResolveConversation
                projectId={selectedConversation.workspace}
                conversationId={selectedConversation._id}
                fetchConversations={fetchConversations}
                resetSelectedConversation={resetSelectedConversation}
              />
            )}
            {allowActiveUserToReassign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.RE_ASSIGN,
                  })
                }
              >
                Reassign
              </Button>
            )}
            {!isViewOnly && (
              <ConversationMoreOptions
                onDeleteConversation={onDeleteConversation}
                onRetryExtraction={onRetryExtraction}
                onArchiveConversation={handleArchiveConversation}
              />
            )}
          </div>
        </div>
        <MessageViewHeaderTabs
          tabs={tabs}
          updateActiveTab={setActiveTab}
        />
      </MessageViewHeader>
      <MessageViewBody>
        {activeTab === MESSAGE_TABS.MESSAGES && (
          <RichTextMessages
            messages={whatsappConversation?.messages}
            projectId={whatsappConversation.workspace}
            conversationId={whatsappConversation._id}
            sourceOfConversation={whatsappConversation.sourceOfConversation}
            assistants={assistants}
            users={users}
          />
        )}
        {activeTab === MESSAGE_TABS.NOTES && (
          <div className="bg-zinc-50 w-full h-full">
            <Notes
              users={users}
              notes={whatsappConversation?.notes}
              conversationId={whatsappConversation._id}
              workspaceId={whatsappConversation.workspace}
              syncConversation={syncConversation}
              isViewOnly={isViewOnly}
            />
          </div>
        )}
      </MessageViewBody>
      {allowActiveUserToSendMessage && (
        <MessageFooter>
          <ChatEditor
            conversation={whatsappConversation}
            updateLocalMessage={updateLocalMessage}
            onSuccess={listener}
            onSend={sendMCubeEngageWhatsappMessage}
          />
        </MessageFooter>
      )}
      {allowActiveUserToStartWithATemplate && <MessageFooter></MessageFooter>}
      {assignAgent.isOpen && (
        <AgentAssign
          mode={assignAgent.mode}
          isOpen={assignAgent.isOpen}
          onClose={() =>
            setAssignAgent({
              isOpen: false,
              mode: "",
            })
          }
          agentId={agentId}
          projectId={selectedConversation.workspace}
          conversationId={selectedConversation._id}
          fetchConversations={fetchConversations}
          resetSelectedConversation={resetSelectedConversation}
          assistantId={selectedConversation.assistant}
        />
      )}
    </MessageView>
  );
}

function RichTextMessage(props) {
  const { conversationId, projectId, sourceOfConversation, ...restProps } =
    props;
  const { message, className, user, timestamp } = restProps;

  if (sourceOfConversation === CONVERSATION.WHATSAPP) {
    const { isMediaMessage, mediaType, mediaId } =
      checkForMediaMessage(message);

    if (isMediaMessage) {
      return MediaMessages[mediaType]({
        projectId: projectId,
        conversationId: conversationId,
        mediaId,
        className,
        user,
        timestamp,
      });
    }
  }

  if (
    sourceOfConversation === CONVERSATION.INSTAGRAM ||
    sourceOfConversation === CONVERSATION.MESSENGER
  ) {
    const { isMediaMessage, mediaType, mediaUrl } =
      checkForInstagramMediaMessage(message);

    if (isMediaMessage) {
      return InstagramMediaMessages[mediaType]({
        mediaUrl,
        className,
        user,
        timestamp,
      });
    }
  }

  return <Message {...restProps} />;
}

function CallDirectionIcon(props) {
  const { direction } = props;

  if (direction === "inbound") {
    return <PhoneArrowDownLeftIcon className="size-6 text-green-500 mt-1" />;
  }

  return <PhoneArrowUpRightIcon className="size-6 text-amber-500" />;
}

export function RichTextMessages(props) {
  const {
    messages,
    projectId,
    conversationId,
    sourceOfConversation,
    assistants,
    users,
    turnMetrics,
  } = props;

  return (
    <div className="p-6 space-y-4">
      {messages?.map((item, index) => {
        return (
          <React.Fragment key={index}>
            <RichTextMessage
              className="flex justify-start"
              message={item?.q ?? ""}
              timestamp={item?.qTimestamp}
              user={false}
              projectId={projectId}
              conversationId={conversationId}
              sourceOfConversation={sourceOfConversation}
              // Instagram: the kind of event this inbound message arrived as (dm / story_reply /
              // story_mention / image / ...). Undefined for other channels, so no badge renders there.
              eventType={item?.eventType}
            />
            <ContextChangeMarker changes={resolveContextChanges(turnMetrics, index)} />
            <RichTextMessage
              className="flex justify-end"
              message={item.a}
              timestamp={item?.aTimestamp}
              user={true}
              projectId={projectId}
              conversationId={conversationId}
              status={item?.whatsAppDetails?.aWStatus}
              errorMessage={item?.whatsAppDetails?.errorMessage}
              sourceOfConversation={sourceOfConversation}
              assistant={
                Boolean(item?.assistant) ? assistants[item.assistant] : ""
              }
              humanAgent={
                Boolean(item?.humanAgent) ? users[item.humanAgent] : ""
              }
            />
            <TurnMetricsLine
              metric={resolveTurnMetric(turnMetrics, index)}
              channel={turnMetrics?.channel}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function TextMessages(props) {
  const { messages, turnMetrics } = props;

  return (
    <div className="p-6 space-y-4">
      {messages?.map((item, index) => (
        <React.Fragment key={index}>
          <Message
            className="flex justify-start"
            message={item.q}
            timestamp={item?.qTimestamp}
            user={false}
          />
          <ContextChangeMarker changes={resolveContextChanges(turnMetrics, index)} />
          <Message
            className="flex justify-end"
            message={item.a}
            timestamp={item?.aTimestamp}
            user={true}
          />
          <TurnMetricsLine
            metric={resolveTurnMetric(turnMetrics, index)}
            channel={turnMetrics?.channel}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

function InstagramMessagesView(props) {
  const {
    selectedConversation,
    onDeleteConversation,
    onRetryExtraction,
    onViewContactClick,
    fetchConversations,
    resetSelectedConversation,
    refreshConversations,
    assistants,
    syncConversation,
    users,
    isViewOnly,
  } = props;

  const { activeTab, setActiveTab, tabs } = useConversationMessagesTabs();
  const user = authService.getCurrentUser();

  const {
    isConversationActive,
    isHumanAgentAssigned,
    allowActiveUserToAssign,
    allowActiveUserToReassign,
    allowActiveUserToResolve,
    allowActiveUserToSendMessage,
  } = useAgentAssignment(selectedConversation, user);

  const {
    agentAssignedConversation,
    assignAgent,
    setAssignAgent,
    updateLocalMessage,
    agentId,
    listener,
  } = useAgentAssignedConversation(
    selectedConversation,
    isConversationActive,
    refreshConversations,
  );

  const { sendInstagramMessage } = useInstagramSend();

  const handleArchiveConversation =
    isConversationActive && !isHumanAgentAssigned
      ? () => archiveConversation(selectedConversation, syncConversation)
      : null;

  return (
    <MessageView key={selectedConversation._id}>
      <MessageViewHeader>
        <div className="flex justify-between items-center">
          <MessageViewContact onContactClick={onViewContactClick}>
            <SourceIcon>
              {
                ConversationSourceIcon[
                  selectedConversation.sourceOfConversation
                ]
              }
            </SourceIcon>
            <MessageViewHeaderTitle>
              {/* Prefer a resolved handle (Instagram @username) when present; otherwise the raw
                  identifier. The attribute is undefined for other channels, so they are unaffected. */}
              {agentAssignedConversation?.attributes?.instagramUsername
                ? `@${agentAssignedConversation.attributes.instagramUsername}`
                : agentAssignedConversation?.userIdentifier ||
                  agentAssignedConversation?.sourceOfConversation}
            </MessageViewHeaderTitle>
          </MessageViewContact>
          <div className="flex gap-x-2 items-center">
            {allowActiveUserToAssign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.ASSIGN,
                  })
                }
              >
                Assign
              </Button>
            )}
            {allowActiveUserToResolve && (
              <ResolveConversation
                projectId={selectedConversation.workspace}
                conversationId={selectedConversation._id}
                fetchConversations={fetchConversations}
                resetSelectedConversation={resetSelectedConversation}
              />
            )}
            {allowActiveUserToReassign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.RE_ASSIGN,
                  })
                }
              >
                Reassign
              </Button>
            )}
            {!isViewOnly && (
              <ConversationMoreOptions
                onDeleteConversation={onDeleteConversation}
                onRetryExtraction={onRetryExtraction}
                onArchiveConversation={handleArchiveConversation}
              />
            )}
          </div>
        </div>
        <MessageViewHeaderTabs
          tabs={tabs}
          updateActiveTab={setActiveTab}
        />
      </MessageViewHeader>
      <MessageViewBody>
        {activeTab === MESSAGE_TABS.MESSAGES && (
          <RichTextMessages
            messages={agentAssignedConversation?.messages}
            projectId={agentAssignedConversation.workspace}
            conversationId={agentAssignedConversation._id}
            sourceOfConversation={
              agentAssignedConversation.sourceOfConversation
            }
            assistants={assistants}
          />
        )}
        {activeTab === MESSAGE_TABS.NOTES && (
          <div className="bg-zinc-50 w-full h-full">
            <Notes
              users={users}
              notes={agentAssignedConversation?.notes}
              conversationId={agentAssignedConversation._id}
              workspaceId={agentAssignedConversation.workspace}
              syncConversation={syncConversation}
              isViewOnly={isViewOnly}
            />
          </div>
        )}
      </MessageViewBody>
      {allowActiveUserToSendMessage && (
        <MessageFooter>
          <ChatEditor
            conversation={agentAssignedConversation}
            updateLocalMessage={updateLocalMessage}
            onSuccess={listener}
            onSend={sendInstagramMessage}
          />
        </MessageFooter>
      )}
      {assignAgent.isOpen && (
        <AgentAssign
          mode={assignAgent.mode}
          isOpen={assignAgent.isOpen}
          onClose={() =>
            setAssignAgent({
              isOpen: false,
              mode: "",
            })
          }
          agentId={agentId}
          projectId={selectedConversation.workspace}
          conversationId={selectedConversation._id}
          fetchConversations={fetchConversations}
          resetSelectedConversation={resetSelectedConversation}
          assistantId={selectedConversation.assistant}
        />
      )}
    </MessageView>
  );
}

function SmsMessagesView(props) {
  const {
    selectedConversation,
    onDeleteConversation,
    onRetryExtraction,
    onViewContactClick,
    fetchConversations,
    resetSelectedConversation,
    refreshConversations,
    assistants,
    syncConversation,
    users,
    isViewOnly,
  } = props;

  const { activeTab, setActiveTab, tabs } = useConversationMessagesTabs();
  const user = authService.getCurrentUser();

  const {
    isConversationActive,
    isHumanAgentAssigned,
    allowActiveUserToAssign,
    allowActiveUserToReassign,
    allowActiveUserToResolve,
    allowActiveUserToSendMessage,
  } = useAgentAssignment(selectedConversation, user);

  const {
    agentAssignedConversation,
    assignAgent,
    setAssignAgent,
    updateLocalMessage,
    agentId,
    listener,
  } = useAgentAssignedConversation(
    selectedConversation,
    isConversationActive,
    refreshConversations,
  );

  const handleArchiveConversation =
    isConversationActive && !isHumanAgentAssigned
      ? () => archiveConversation(selectedConversation, syncConversation)
      : null;

  const sendSmsMessage = async function (data, onSuccess) {
    const assistantId = selectedConversation.assistant;
    const message = data.message;

    const payload = {
      fromNumber: selectedConversation.fromIdentifier,
      toNumber: selectedConversation.userIdentifier,
      workspaceId: selectedConversation.workspace,
      message,
    };

    try {
      const response = await postAddToSMSThread({ assistantId, payload });

      if (response.status === 200) {
        onSuccess();
      }
    } catch (e) {
      showErrorToast(e, "Failed to send message");
    }
  };

  return (
    <MessageView key={selectedConversation._id}>
      <MessageViewHeader>
        <div className="flex justify-between items-center">
          <MessageViewContact onContactClick={onViewContactClick}>
            <SourceIcon>
              {
                ConversationSourceIcon[
                  selectedConversation.sourceOfConversation
                ]
              }
            </SourceIcon>
            <MessageViewHeaderTitle>
              {/* Prefer a resolved handle (Instagram @username) when present; otherwise the raw
                  identifier. The attribute is undefined for other channels, so they are unaffected. */}
              {agentAssignedConversation?.attributes?.instagramUsername
                ? `@${agentAssignedConversation.attributes.instagramUsername}`
                : agentAssignedConversation?.userIdentifier ||
                  agentAssignedConversation?.sourceOfConversation}
            </MessageViewHeaderTitle>
          </MessageViewContact>
          <div className="flex gap-x-2 items-center">
            {allowActiveUserToAssign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.ASSIGN,
                  })
                }
              >
                Assign
              </Button>
            )}
            {allowActiveUserToResolve && (
              <ResolveConversation
                projectId={selectedConversation.workspace}
                conversationId={selectedConversation._id}
                fetchConversations={fetchConversations}
                resetSelectedConversation={resetSelectedConversation}
              />
            )}
            {allowActiveUserToReassign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.RE_ASSIGN,
                  })
                }
              >
                Reassign
              </Button>
            )}
            {!isViewOnly && (
              <ConversationMoreOptions
                onDeleteConversation={onDeleteConversation}
                onRetryExtraction={onRetryExtraction}
                onArchiveConversation={handleArchiveConversation}
              />
            )}
          </div>
        </div>
        <MessageViewHeaderTabs
          tabs={tabs}
          updateActiveTab={setActiveTab}
        />
      </MessageViewHeader>
      <MessageViewBody>
        {activeTab === MESSAGE_TABS.MESSAGES && (
          <RichTextMessages
            messages={agentAssignedConversation?.messages}
            projectId={agentAssignedConversation.workspace}
            conversationId={agentAssignedConversation._id}
            sourceOfConversation={
              agentAssignedConversation.sourceOfConversation
            }
            assistants={assistants}
          />
        )}
        {activeTab === MESSAGE_TABS.NOTES && (
          <div className="bg-zinc-50 w-full h-full">
            <Notes
              users={users}
              notes={agentAssignedConversation?.notes}
              conversationId={agentAssignedConversation._id}
              workspaceId={agentAssignedConversation.workspace}
              syncConversation={syncConversation}
              isViewOnly={isViewOnly}
            />
          </div>
        )}
      </MessageViewBody>
      {allowActiveUserToSendMessage && (
        <MessageFooter>
          <ChatEditor
            conversation={agentAssignedConversation}
            updateLocalMessage={updateLocalMessage}
            onSuccess={listener}
            onSend={(x, y, data, onSuccess) => {
              sendSmsMessage(data, onSuccess);
            }}
          />
        </MessageFooter>
      )}
      {assignAgent.isOpen && (
        <AgentAssign
          mode={assignAgent.mode}
          isOpen={assignAgent.isOpen}
          onClose={() =>
            setAssignAgent({
              isOpen: false,
              mode: "",
            })
          }
          agentId={agentId}
          projectId={selectedConversation.workspace}
          conversationId={selectedConversation._id}
          fetchConversations={fetchConversations}
          resetSelectedConversation={resetSelectedConversation}
          assistantId={selectedConversation.assistant}
        />
      )}
    </MessageView>
  );
}

function MessengerMessagesView(props) {
  const {
    selectedConversation,
    onDeleteConversation,
    onRetryExtraction,
    onViewContactClick,
    fetchConversations,
    resetSelectedConversation,
    refreshConversations,
    assistants,
    syncConversation,
    users,
    isViewOnly,
  } = props;

  const { activeTab, setActiveTab, tabs } = useConversationMessagesTabs();
  const user = authService.getCurrentUser();

  const {
    isConversationActive,
    isHumanAgentAssigned,
    allowActiveUserToAssign,
    allowActiveUserToReassign,
    allowActiveUserToResolve,
    allowActiveUserToSendMessage,
  } = useAgentAssignment(selectedConversation, user);

  const {
    agentAssignedConversation,
    assignAgent,
    setAssignAgent,
    updateLocalMessage,
    agentId,
    listener,
  } = useAgentAssignedConversation(
    selectedConversation,
    isConversationActive,
    refreshConversations,
  );

  const { sendMessengerMessage } = useMessengerSend();

  const handleArchiveConversation =
    isConversationActive && !isHumanAgentAssigned
      ? () => archiveConversation(selectedConversation, syncConversation)
      : null;

  return (
    <MessageView key={selectedConversation._id}>
      <MessageViewHeader>
        <div className="flex justify-between items-center">
          <MessageViewContact onContactClick={onViewContactClick}>
            <SourceIcon>
              {
                ConversationSourceIcon[
                  selectedConversation.sourceOfConversation
                ]
              }
            </SourceIcon>
            <MessageViewHeaderTitle>
              {/* Prefer a resolved handle (Instagram @username) when present; otherwise the raw
                  identifier. The attribute is undefined for other channels, so they are unaffected. */}
              {agentAssignedConversation?.attributes?.instagramUsername
                ? `@${agentAssignedConversation.attributes.instagramUsername}`
                : agentAssignedConversation?.userIdentifier ||
                  agentAssignedConversation?.sourceOfConversation}
            </MessageViewHeaderTitle>
          </MessageViewContact>
          <div className="flex gap-x-2 items-center">
            {allowActiveUserToAssign && !isViewOnly && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.ASSIGN,
                  })
                }
              >
                Assign
              </Button>
            )}
            {allowActiveUserToResolve && (
              <ResolveConversation
                projectId={selectedConversation.workspace}
                conversationId={selectedConversation._id}
                fetchConversations={fetchConversations}
                resetSelectedConversation={resetSelectedConversation}
              />
            )}
            {allowActiveUserToReassign && (
              <Button
                outline
                onClick={() =>
                  setAssignAgent({
                    isOpen: true,
                    mode: AssignmentMode.RE_ASSIGN,
                  })
                }
              >
                Reassign
              </Button>
            )}
            {!isViewOnly && (
              <ConversationMoreOptions
                onDeleteConversation={onDeleteConversation}
                onRetryExtraction={onRetryExtraction}
                onArchiveConversation={handleArchiveConversation}
              />
            )}
          </div>
        </div>
        <MessageViewHeaderTabs
          tabs={tabs}
          updateActiveTab={setActiveTab}
        />
      </MessageViewHeader>
      <MessageViewBody>
        {activeTab === MESSAGE_TABS.MESSAGES && (
          <RichTextMessages
            messages={agentAssignedConversation?.messages}
            projectId={agentAssignedConversation.workspace}
            conversationId={agentAssignedConversation._id}
            sourceOfConversation={
              agentAssignedConversation.sourceOfConversation
            }
            assistants={assistants}
          />
        )}
        {activeTab === MESSAGE_TABS.NOTES && (
          <div className="bg-zinc-50 w-full h-full">
            <Notes
              users={users}
              notes={agentAssignedConversation?.notes}
              conversationId={agentAssignedConversation._id}
              workspaceId={agentAssignedConversation.workspace}
              syncConversation={syncConversation}
              isViewOnly={isViewOnly}
            />
          </div>
        )}
      </MessageViewBody>
      {allowActiveUserToSendMessage && (
        <MessageFooter>
          <ChatEditor
            conversation={agentAssignedConversation}
            updateLocalMessage={updateLocalMessage}
            onSuccess={listener}
            onSend={sendMessengerMessage}
          />
        </MessageFooter>
      )}
      {assignAgent.isOpen && (
        <AgentAssign
          mode={assignAgent.mode}
          isOpen={assignAgent.isOpen}
          onClose={() =>
            setAssignAgent({
              isOpen: false,
              mode: "",
            })
          }
          agentId={agentId}
          projectId={selectedConversation.workspace}
          conversationId={selectedConversation._id}
          fetchConversations={fetchConversations}
          resetSelectedConversation={resetSelectedConversation}
          assistantId={selectedConversation.assistant}
        />
      )}
    </MessageView>
  );
}

export function VoiceConversationMessagesView(props) {
  const {
    selectedConversation,
    onDeleteConversation,
    onRetryExtraction,
    onViewContactClick,
    syncConversation,
    users,
    isViewOnly,
    turnMetrics,
  } = props;

  const { activeTab, setActiveTab, tabs } = useConversationMessagesTabs();
  const {
    assistantTranscriptions,
    humanAgentTranscriptions,
    isCallTransfered,
  } = useAgentTranscription(selectedConversation?.messages ?? []);

  const isConversationActive = selectedConversation?.status === "ACTIVE";

  const handleArchiveConversation = isConversationActive
    ? () => archiveConversation(selectedConversation, syncConversation)
    : null;

  return (
    <MessageView key={selectedConversation?._id}>
      <MessageViewHeader>
        <div className="flex justify-between items-center">
          <MessageViewContact onContactClick={onViewContactClick}>
            <SourceIcon>
              {
                ConversationSourceIcon[
                  selectedConversation?.sourceOfConversation
                ]
              }
            </SourceIcon>
            <MessageViewHeaderTitle>
              {selectedConversation?.userIdentifier ||
                selectedConversation?.sourceOfConversation}
            </MessageViewHeaderTitle>
          </MessageViewContact>
          {!isViewOnly && (
            <ConversationMoreOptions
              onDeleteConversation={onDeleteConversation}
              onRetryExtraction={onRetryExtraction}
              onArchiveConversation={handleArchiveConversation}
            />
          )}
        </div>
        <MessageViewHeaderTabs
          tabs={tabs}
          updateActiveTab={setActiveTab}
        />
      </MessageViewHeader>
      <MessageViewBody>
        {activeTab === MESSAGE_TABS.MESSAGES && (
          <>
            <TextMessages
              messages={assistantTranscriptions}
              turnMetrics={turnMetrics}
            />
            {isCallTransfered && (
              <div className="p-4">
                <div className="rounded-lg">
                  <div className="bg-green-100 px-4 py-2 rounded-t-lg flex items-center gap-x-1">
                    <MdSupportAgent className="size-6" />
                    <p className="text-sm">Human Agent</p>
                  </div>
                  <div className="bg-slate-100 rounded-b-lg">
                    <TextMessages messages={humanAgentTranscriptions} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {activeTab === MESSAGE_TABS.NOTES && (
          <div className="bg-zinc-50 w-full h-full">
            <Notes
              users={users}
              notes={selectedConversation?.notes}
              conversationId={selectedConversation?._id}
              workspaceId={selectedConversation?.workspace}
              syncConversation={syncConversation}
              isViewOnly={isViewOnly}
            />
          </div>
        )}
      </MessageViewBody>
    </MessageView>
  );
}

export function ConversationMessagesView(props) {
  const {
    selectedConversation,
    onDeleteConversation,
    onRetryExtraction,
    onViewContactClick,
    syncConversation,
    users,
    isViewOnly,
    turnMetrics,
  } = props;

  const { activeTab, setActiveTab, tabs } = useConversationMessagesTabs();

  const isConversationActive = selectedConversation?.status === "ACTIVE";

  const handleArchiveConversation = isConversationActive
    ? () => archiveConversation(selectedConversation, syncConversation)
    : null;

  return (
    <MessageView key={selectedConversation?._id}>
      <MessageViewHeader>
        <div className="flex justify-between items-center">
          <MessageViewContact onContactClick={onViewContactClick}>
            <SourceIcon>
              {
                ConversationSourceIcon[
                  selectedConversation?.sourceOfConversation
                ]
              }
            </SourceIcon>
            <MessageViewHeaderTitle>
              {selectedConversation?.userIdentifier ||
                selectedConversation?.sourceOfConversation}
            </MessageViewHeaderTitle>
          </MessageViewContact>
          {!isViewOnly && (
            <ConversationMoreOptions
              onDeleteConversation={onDeleteConversation}
              onRetryExtraction={onRetryExtraction}
              onArchiveConversation={handleArchiveConversation}
            />
          )}
        </div>
        <MessageViewHeaderTabs
          tabs={tabs}
          updateActiveTab={setActiveTab}
        />
      </MessageViewHeader>
      <MessageViewBody>
        {activeTab === MESSAGE_TABS.MESSAGES && (
          <TextMessages
            messages={selectedConversation?.messages}
            turnMetrics={turnMetrics}
          />
        )}
        {activeTab === MESSAGE_TABS.NOTES && (
          <div className="bg-zinc-50 w-full h-full">
            <Notes
              users={users}
              notes={selectedConversation?.notes}
              conversationId={selectedConversation?._id}
              workspaceId={selectedConversation?.workspace}
              syncConversation={syncConversation}
              isViewOnly={isViewOnly}
            />
          </div>
        )}
      </MessageViewBody>
    </MessageView>
  );
}

// Humanise an Instagram message eventType for the badge. Returns null for plain DMs (the default) so
// only the noteworthy kinds - story replies/mentions, shares, media - get a label in the inbox.
function formatInstagramEventType(eventType) {
  if (!eventType || eventType === "dm") return null;
  const labels = {
    story_reply: "Story reply",
    story_mention: "Story mention",
    ig_reel: "Reel",
    reel: "Reel",
    share: "Shared post",
    image: "Image",
    video: "Video",
    audio: "Audio",
    file: "File",
  };
  return labels[eventType] || String(eventType).replace(/_/g, " ");
}

function Message(props) {
  const {
    message,
    timestamp,
    className,
    user,
    status,
    assistant,
    errorMessage,
    humanAgent,
    customBubbleColor,
    eventType,
  } = props;

  if (message.trim() === "") {
    return null;
  }

  const bubbleColor =
    customBubbleColor ??
    (user ? "bg-blue-600 text-gray-100" : "bg-blue-100 text-gray-700");

  const eventTypeLabel = formatInstagramEventType(eventType);

  return (
    <div className={className}>
      <div className="flex flex-col items-end">
        <div className={`max-w-sm md:max-w-md rounded-lg ${bubbleColor} p-4`}>
          {assistant && (
            <p className="text-tiny md:text-sm font-semibold mb-2">
              {assistant}
            </p>
          )}
          {humanAgent && (
            <p className="text-tiny md:text-sm font-semibold mb-2">
              {humanAgent.firstName}
            </p>
          )}
          {eventTypeLabel && (
            <div className="mb-2">
              <Badge color="indigo">{eventTypeLabel}</Badge>
            </div>
          )}
          <p className="text-sm break-words whitespace-pre-line">{message}</p>
          {timestamp && (
            <div className="flex justify-end mt-2">
              <span className="text-tiny md:text-xs">
                {formatTimestamp(timestamp)}
              </span>
            </div>
          )}
        </div>
        {status && status !== "failed" && (
          <div className="flex justify-end mt-2">
            <Badge color={STATUS[status]?.color}>
              {STATUS[status]?.label ?? status}
            </Badge>
          </div>
        )}
        {status && status === "failed" && (
          <Tooltip title={errorMessage}>
            <div className="flex justify-end mt-2 items-center gap-x-2 hover:cursor-pointer">
              <Badge color={STATUS[status].color}>{STATUS[status].label}</Badge>
              <InformationCircleIcon className="size-4 text-red-500 hover:cursor-pointer" />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function MessageViewHeader({ children, resetSelectedConversation }) {
  return (
    <div className="flex flex-col gap-y-4 px-6 pt-6 border-b border-gray-200 sticky top-0">
      {children}
    </div>
  );
}

function MessageViewHeaderTabs({ tabs, updateActiveTab }) {
  return (
    <TabsWithBadge
      tabs={tabs}
      onTabChange={(e, { tab, index }) => updateActiveTab(tab.name)}
    />
  );
}

function MessageViewHeaderTitle({ children }) {
  return <h2 className="text-xl font-semibold text-gray-800">{children}</h2>;
}

function MessageViewContact({ onContactClick, children }) {
  return (
    <div
      onClick={onContactClick}
      className="flex gap-x-2 cursor-pointer"
    >
      {children}
    </div>
  );
}

const MessageViewBody = forwardRef(({ children }, ref) => {
  const scrollRef = useRef(null);

  useImperativeHandle(
    ref,
    function () {
      return {
        scrollToBottom() {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        },
      };
    },
    [],
  );

  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  return (
    <div
      ref={scrollRef}
      className="overflow-y-auto flex-grow space-y-4"
    >
      {children}
    </div>
  );
});

function MessageView({ id, children }) {
  return <div className="border-r flex flex-col flex-grow">{children}</div>;
}

function MessageFooter({ children }) {
  return <>{children}</>;
}
export {
  Message,
  MessageFooter,
  MessageView,
  MessageViewBody,
  MessageViewContact,
  MessageViewHeader,
  MessageViewHeaderTitle,
};
